import React, { useState, useEffect, useMemo, useRef } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Notification from '../../components/Notification';
import Modal from '../../components/Modal/Modal';
import { useUsers } from '../../hooks/useUsers';
import './AdminPage.css';
import {
    ROLES,
    INSPECTION_AREAS,
    INSURANCE_COMPANIES,
    ALL_INSURANCE_COMPANIES
} from '../../utils/constants';
import { useSpinner } from '../../context/SpinnerContext';
import logger from '../../utils/logger';

const { INSPECTOR, CREATOR, CREATOR_SENIOR, ADMINISTRATOR } = ROLES;
const { ANTECEDENTS, VERDICTS, VISUAL_INSPECTION } = INSPECTION_AREAS;

const AdminPage = () => {
    const {
        users,
        loading,
        error,
        createUser,
        deleteUser,
        toggleUserStatus,
        loadUsers
    } = useUsers();

    const { showSpinner, hideSpinner } = useSpinner();

    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [notification, setNotification] = useState({ visible: false, message: '', type: 'info' });
    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        role: INSPECTOR,
        status: 'activo',
        insuranceCompanies: [],
        inspectionAreas: []
    });
    const [selectedRoleFilter, setSelectedRoleFilter] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
    const companyDropdownRef = useRef(null);

    const roles = [
        { value: CREATOR, label: 'Creador' },
        { value: INSPECTOR, label: 'Inspector' },
        { value: CREATOR_SENIOR, label: 'Creador Senior' },
        { value: ADMINISTRATOR, label: 'Administrador' }
    ];

    const estados = [
        { value: 'activo', label: 'Activo' },
        { value: 'inactivo', label: 'Inactivo' },
        { value: 'suspendido', label: 'Suspendido' }
    ];

    const showNotification = (message, type = 'info') => {
        setNotification({ visible: true, message, type });
        setTimeout(() => {
            setNotification({ visible: false, message: '', type: 'info' });
        }, 5000);
    };

    const clearForm = () => {
        setNewUser({
            name: '',
            email: '',
            role: INSPECTOR,
            status: 'activo',
            insuranceCompanies: [],
            inspectionAreas: []
        });
        setIsCompanyDropdownOpen(false);
    };

    const handleCreateUser = async () => {
        try {
            showSpinner('Creando usuario...');
            const { insuranceCompanies, ...rest } = newUser;
            const payload = {
                ...rest,
                insuranceCompany: insuranceCompanies.join(','),
                temporaryPassword: 'Temporal1*'
            };
            logger.info('Creando usuario...', {
                newUser: payload,
            }, 'AdminPage');
            await createUser(payload);
            clearForm();
            setShowCreateUserModal(false);
            showNotification('Usuario creado exitosamente', 'success');
        } catch (error) {
            logger.error('Error al crear usuario...', error, {
                newUser: newUser,
            }, 'AdminPage');
            showNotification(error.message || 'Error al crear usuario', 'error');
        } finally {
            hideSpinner();
        }
    };

    const handleInputChange = (field, value) => {
        setNewUser(prev => {
            const updated = { ...prev, [field]: value };

            if (field === 'role' && value !== INSPECTOR) {
                updated.inspectionAreas = [];
            }

            if (field === 'role' && ![INSPECTOR, CREATOR, CREATOR_SENIOR].includes(value)) {
                updated.insuranceCompanies = [];
            }

            return updated;
        });
    };

    const isAllCompaniesSelected = ALL_INSURANCE_COMPANIES.every(company =>
        newUser.insuranceCompanies?.includes(company)
    );

    const getCompanySelectLabel = () => {
        const selected = newUser.insuranceCompanies || [];
        if (selected.length === 0) return 'Selecciona compañías';
        if (isAllCompaniesSelected) {
            const hasOtra = selected.includes('OTRA');
            return hasOtra ? 'TODAS, OTRA' : 'TODAS';
        }
        return selected.join(', ');
    };

    const handleCompanyToggle = (company) => {
        setNewUser(prev => {
            const current = prev.insuranceCompanies || [];
            const next = current.includes(company)
                ? current.filter(c => c !== company)
                : [...current, company];
            return { ...prev, insuranceCompanies: next };
        });
    };

    const handleAllCompaniesToggle = () => {
        setNewUser(prev => {
            const current = prev.insuranceCompanies || [];
            const allSelected = ALL_INSURANCE_COMPANIES.every(c => current.includes(c));
            if (allSelected) {
                return {
                    ...prev,
                    insuranceCompanies: current.filter(c => !ALL_INSURANCE_COMPANIES.includes(c))
                };
            }
            const withoutAll = current.filter(c => !ALL_INSURANCE_COMPANIES.includes(c));
            return {
                ...prev,
                insuranceCompanies: [...withoutAll, ...ALL_INSURANCE_COMPANIES]
            };
        });
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteConfirmModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;

        try {
            showSpinner('Eliminando usuario...');
            logger.info('Eliminando usuario...', {
                userToDelete: userToDelete,
            }, 'AdminPage');
            await deleteUser(userToDelete.id);
            setShowDeleteConfirmModal(false);
            setUserToDelete(null);
            showNotification('Usuario eliminado exitosamente', 'success');
        } catch (error) {
            logger.error('Error al eliminar usuario...', error, null, 'AdminPage');
            showNotification(error.message || 'Error al eliminar usuario', 'error');
        } finally {
            hideSpinner();
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirmModal(false);
        setUserToDelete(null);
    };

    const handleStatusChange = async (userId, newStatus) => {
        try {
            showSpinner('Actualizando estado del usuario...');
            logger.info('Actualizando estado del usuario...', {
                userId: userId,
                newStatus: newStatus,
            }, 'AdminPage');
            await toggleUserStatus(userId, newStatus);
            logger.info('Estado del usuario actualizado exitosamente...', {
                userId: userId,
                newStatus: newStatus,
            }, 'AdminPage');
            showNotification('Estado del usuario actualizado exitosamente', 'success');
        } catch (error) {
            logger.error('Error al cambiar estado del usuario...', error, null, 'AdminPage');
            showNotification(error.message || 'Error al cambiar estado del usuario', 'error');
        } finally {
            hideSpinner();
        }
    };

    const getStatusColor = (estado) => {
        switch (estado) {
            case 'activo': return '#28a745';
            case 'inactivo': return '#6c757d';
            case 'suspendido': return '#dc3545';
            default: return '#6c757d';
        }
    };

    const getRoleColor = (rol) => {
        switch (rol) {
            case ADMINISTRATOR: return '#dc3545';
            case INSPECTOR: return '#007bff';
            case CREATOR: return '#28a745';
            case CREATOR_SENIOR: return '#17a2b8';
            default: return '#6c757d';
        }
    };

    const handleCloseModal = () => {
        clearForm();
        setShowCreateUserModal(false);
    };

    const handleInspectionAreaChange = (area) => {
        setNewUser(prev => ({
            ...prev,
            inspectionAreas: prev.inspectionAreas?.includes(area)
                ? prev.inspectionAreas.filter(a => a !== area)
                : [...prev.inspectionAreas, area]
        }));
    };

    const validateForm = () => {
        if (!newUser.name) return false;
        if (!newUser.email) return false;
        if (!newUser.role) return false;
        if (!newUser.status) return false;
        if ([INSPECTOR, CREATOR, CREATOR_SENIOR].includes(newUser.role) && newUser.insuranceCompanies?.length === 0) return false;
        if (newUser.role === INSPECTOR && newUser.inspectionAreas?.length === 0) return false;
        return true;
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
                setIsCompanyDropdownOpen(false);
            }
        };

        if (isCompanyDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isCompanyDropdownOpen]);

    useEffect(() => {
        if (loading) {
            showSpinner('Cargando usuarios...');
        } else {
            hideSpinner();
        }
    }, [loading, showSpinner, hideSpinner]);

    useEffect(() => {
        return () => hideSpinner();
    }, [hideSpinner]);

    const usersFilteredByRole = useMemo(() => {
        if (selectedRoleFilter === null) return users;
        return users.filter(u => u.role === selectedRoleFilter);
    }, [users, selectedRoleFilter]);

    const filteredUsers = useMemo(() => {
        const term = searchText.trim().toLowerCase();
        if (!term) return usersFilteredByRole;
        return usersFilteredByRole.filter(user =>
            (user.name && user.name.toLowerCase().includes(term)) ||
            (user.email && user.email.toLowerCase().includes(term))
        );
    }, [usersFilteredByRole, searchText]);

    return (
        <div className="admin-page">
            <Header />

            <main className="admin-content-wrapper">
                {/* Page Title */}
                <div className="page-title-section">
                    <div className="title-content">
                        <div className="title-row">
                            <svg className="admin-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                            <h1>Panel de Administración</h1>
                        </div>
                        <p className="page-subtitle">Gestiona usuarios, roles y permisos del sistema de inspección</p>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="stats-section">
                    <div
                        className={`stat-card ${selectedRoleFilter === null ? 'stat-card--selected' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedRoleFilter(null)}
                        onKeyDown={(e) => e.key === 'Enter' && setSelectedRoleFilter(null)}
                    >
                        <div className="stat-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <h3>{users.length}</h3>
                            <p>Total Usuarios</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 12l2 2 4-4"></path>
                                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"></path>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <h3>{users.filter(u => u.status === 'activo').length}</h3>
                            <p>Usuarios Activos</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 12l2 2 4-4"></path>
                                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"></path>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <h3>{users.filter(u => u.status === 'inactivo').length}</h3>
                            <p>Usuarios Inactivos</p>
                        </div>
                    </div>

                    <div
                        className={`stat-card ${selectedRoleFilter === ADMINISTRATOR ? 'stat-card--selected' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedRoleFilter(ADMINISTRATOR)}
                        onKeyDown={(e) => e.key === 'Enter' && setSelectedRoleFilter(ADMINISTRATOR)}
                    >
                        <div className="stat-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <h3>{users.filter(u => u.role === ADMINISTRATOR).length}</h3>
                            <p>Administradores</p>
                        </div>
                    </div>

                    <div
                        className={`stat-card ${selectedRoleFilter === INSPECTOR ? 'stat-card--selected' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedRoleFilter(INSPECTOR)}
                        onKeyDown={(e) => e.key === 'Enter' && setSelectedRoleFilter(INSPECTOR)}
                    >
                        <div className="stat-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <h3>{users.filter(u => u.role === INSPECTOR).length}</h3>
                            <p>Inspectores</p>
                        </div>
                    </div>

                    <div
                        className={`stat-card ${selectedRoleFilter === CREATOR ? 'stat-card--selected' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedRoleFilter(CREATOR)}
                        onKeyDown={(e) => e.key === 'Enter' && setSelectedRoleFilter(CREATOR)}
                    >
                        <div className="stat-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14,2 14,8 20,8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10,9 9,9 8,9"></polyline>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <h3>{users.filter(u => u.role === CREATOR).length}</h3>
                            <p>Creadores</p>
                        </div>
                    </div>

                    <div
                        className={`stat-card ${selectedRoleFilter === CREATOR_SENIOR ? 'stat-card--selected' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedRoleFilter(CREATOR_SENIOR)}
                        onKeyDown={(e) => e.key === 'Enter' && setSelectedRoleFilter(CREATOR_SENIOR)}
                    >
                        <div className="stat-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14,2 14,8 20,8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10,9 9,9 8,9"></polyline>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <h3>{users.filter(u => u.role === CREATOR_SENIOR).length}</h3>
                            <p>Creadores Senior</p>
                        </div>
                    </div>
                </div>

                {/* Users Management Section */}
                <div className="section-card">
                    <div className="section-header">
                        <h2 className="section-title">Gestión de Usuarios</h2>
                        <div className="section-header-actions">
                            <input
                                type="text"
                                className="admin-search-input"
                                placeholder="Buscar por usuario o email"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <button
                                className="btn-admin btn-admin-primary"
                                onClick={() => setShowCreateUserModal(true)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 5v14"></path>
                                    <path d="M5 12h14"></path>
                                </svg>
                                Crear Usuario
                            </button>
                        </div>
                    </div>

                    <div className="users-table-container">
                        {error ? (
                            <div className="error-container">
                                <p>Error: {error}</p>
                                <button className="btn-admin btn-admin-primary" onClick={loadUsers}>
                                    Reintentar
                                </button>
                            </div>
                        ) : (
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>Usuario</th>
                                        <th>Email</th>
                                        <th>Rol</th>
                                        <th>Áreas</th>
                                        <th>Compañía</th>
                                        <th>Estado</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="no-data">
                                                {users.length === 0 ? 'No hay usuarios registrados' : 'No hay resultados para el filtro aplicado'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredUsers.map(user => (
                                            <tr key={user.id}>
                                                <td>
                                                    <div className="user-info">
                                                        <div className="user-avatar">
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                                <circle cx="12" cy="7" r="4"></circle>
                                                            </svg>
                                                        </div>
                                                        <div className="user-details">
                                                            <span className="user-name">{user.name}</span>
                                                            <span className="user-id">ID: {user.id}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{user.email}</td>
                                                <td>
                                                    <span
                                                        className="role-badge"
                                                        style={{ backgroundColor: getRoleColor(user.role) }}
                                                    >
                                                        {user.role}
                                                    </span>
                                                </td>
                                                <td>{user.inspectionAreas?.map(area => {
                                                    if (area === ANTECEDENTS) return 'Antecedentes';
                                                    if (area === VERDICTS) return 'Dictámenes';
                                                    if (area === VISUAL_INSPECTION) return 'Inspección Visual';
                                                    return area;
                                                }).join(', ') || 'N/A'}</td>
                                                <td>{user.insuranceCompany ? user.insuranceCompany.split(',').join(', ') : 'N/A'}</td>
                                                <td>
                                                    <span
                                                        className="status-badge"
                                                        style={{ backgroundColor: getStatusColor(user.status) }}
                                                    >
                                                        {user.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            className="btn-admin btn-admin-sm btn-admin-secondary"
                                                            onClick={() => handleStatusChange(user.id, user.status === 'activo' ? 'inactivo' : 'activo')}
                                                        >
                                                            {user.status === 'activo' ? 'Desactivar' : 'Activar'}
                                                        </button>
                                                        <button
                                                            className="btn-admin btn-admin-sm btn-admin-danger"
                                                            onClick={() => handleDeleteClick(user)}
                                                        >
                                                            Eliminar
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </main>

            <Footer />

            {/* Create User Modal */}
            <Modal
                isOpen={showCreateUserModal}
                onClose={handleCloseModal}
                title="Crear Nuevo Usuario"
            >
                <div className="modal-form">
                    <div className="form-group">
                        <label htmlFor="nombre">Nombre Completo *</label>
                        <input
                            type="text"
                            id="nombre"
                            value={newUser.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Juan Pérez"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                            type="email"
                            id="email"
                            value={newUser.email.toLowerCase()}
                            onChange={(e) => handleInputChange('email', e.target.value.toLowerCase())}
                            placeholder="juan.perez@ivisnk9.com"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="rol">Rol *</label>
                        <select
                            id="rol"
                            value={newUser.role}
                            onChange={(e) => handleInputChange('role', e.target.value)}
                            required
                        >
                            {roles.map(role => (
                                <option key={role.value} value={role.value}>
                                    {role.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {newUser.role === INSPECTOR && (
                        <div className="form-group">
                            <label>Áreas de Inspección *</label>
                            <div className="checkbox-group">
                                <div className="checkbox-item">
                                    <input
                                        type="checkbox"
                                        id="antecedents"
                                        checked={newUser.inspectionAreas?.includes(ANTECEDENTS)}
                                        onChange={() => handleInspectionAreaChange(ANTECEDENTS)}
                                    />
                                    <label htmlFor="antecedents">Antecedentes</label>
                                </div>
                                <div className="checkbox-item">
                                    <input
                                        type="checkbox"
                                        id="verdicts"
                                        checked={newUser.inspectionAreas?.includes(VERDICTS)}
                                        onChange={() => handleInspectionAreaChange(VERDICTS)}
                                    />
                                    <label htmlFor="verdicts">Dictámenes</label>
                                </div>
                                <div className="checkbox-item">
                                    <input
                                        type="checkbox"
                                        id="visualInspection"
                                        checked={newUser.inspectionAreas?.includes(VISUAL_INSPECTION)}
                                        onChange={() => handleInspectionAreaChange(VISUAL_INSPECTION)}
                                    />
                                    <label htmlFor="visualInspection">Inspección Visual</label>
                                </div>
                            </div>
                            {newUser.role === INSPECTOR && newUser.inspectionAreas?.length === 0 && (
                                <span className="error-message">Debe seleccionar al menos un área de inspección</span>
                            )}
                        </div>
                    )}

                    {[INSPECTOR, CREATOR, CREATOR_SENIOR].includes(newUser.role) && (
                        <div className="form-group">
                            <label htmlFor="insuranceCompany">Nombre de la Compañía *</label>
                            <div className="multi-select" ref={companyDropdownRef}>
                                <button
                                    type="button"
                                    id="insuranceCompany"
                                    className="multi-select-trigger"
                                    onClick={() => setIsCompanyDropdownOpen(prev => !prev)}
                                    aria-expanded={isCompanyDropdownOpen}
                                    aria-haspopup="listbox"
                                >
                                    <span className={newUser.insuranceCompanies?.length ? '' : 'multi-select-placeholder'}>
                                        {getCompanySelectLabel()}
                                    </span>
                                    <span className="multi-select-chevron" aria-hidden="true">▾</span>
                                </button>
                                {isCompanyDropdownOpen && (
                                    <div className="multi-select-dropdown" role="listbox" aria-multiselectable="true">
                                        <div className="multi-select-options-list">
                                            <label className="multi-select-option multi-select-option-all">
                                                <input
                                                    type="checkbox"
                                                    checked={isAllCompaniesSelected}
                                                    onChange={handleAllCompaniesToggle}
                                                />
                                                <span>TODAS</span>
                                            </label>
                                            {INSURANCE_COMPANIES.map(company => (
                                                <label key={company.value} className="multi-select-option">
                                                    <input
                                                        type="checkbox"
                                                        checked={newUser.insuranceCompanies?.includes(company.value)}
                                                        onChange={() => handleCompanyToggle(company.value)}
                                                    />
                                                    <span>{company.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {newUser.insuranceCompanies?.length === 0 && (
                                <span className="error-message">Debe seleccionar al menos una compañía</span>
                            )}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="estado">Estado *</label>
                        <select
                            id="estado"
                            value={newUser.status}
                            onChange={(e) => handleInputChange('status', e.target.value)}
                            required
                        >
                            {estados.map(estado => (
                                <option key={estado.value} value={estado.value}>
                                    {estado.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="modal-actions">
                        <button
                            className="btn-admin btn-admin-secondary"
                            onClick={handleCloseModal}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn-admin btn-admin-primary"
                            onClick={handleCreateUser}
                            disabled={!validateForm()}
                        >
                            Crear Usuario
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteConfirmModal}
                onClose={handleCancelDelete}
                title="Confirmar Eliminación"
            >
                <div className="delete-confirmation">
                    <div className="warning-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                    </div>

                    <h3>¿Estás seguro de que quieres eliminar este usuario?</h3>

                    {userToDelete && (
                        <div className="user-to-delete">
                            <p><strong>Usuario:</strong> {userToDelete.name}</p>
                            <p><strong>Email:</strong> {userToDelete.email}</p>
                            <p><strong>Rol:</strong> {userToDelete.role}</p>
                        </div>
                    )}

                    <p className="warning-message">
                        Esta acción no se puede deshacer. El usuario será eliminado permanentemente del sistema.
                    </p>

                    <div className="modal-actions">
                        <button
                            className="btn-admin btn-admin-secondary"
                            onClick={handleCancelDelete}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn-admin btn-admin-danger"
                            onClick={handleConfirmDelete}
                        >
                            Eliminar Usuario
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Notification Component */}
            <Notification
                visible={notification.visible}
                message={notification.message}
                type={notification.type}
                onClose={() => setNotification({ visible: false, message: '', type: 'info' })}
            />
        </div>
    );
};

export default AdminPage;